/**
 * city_night_lights — WHERE a town's lights are at night, and how bright each one is (the night-sky realism wave,
 * 2026-10-06; agent L). PURE (no three, no DOM): the light carpet's points from the baked map file, the level-of-detail
 * aggregation, the lit-window schedule over the night, and the CPU mirrors of every number the shader computes
 * (`CityNightLightsModule` draws them; `verify:city-lights` pins this file).
 *
 * WHAT A CITY AT NIGHT IS (the references): from a distance a town is a carpet of warm POINTS — street lamps along its
 * grid and lit windows — brighter and denser at the centre, under an amber haze lit from below (NOIRLab's Athens: the
 * streets "meander through the city like illuminated rivers", sodium orange with white LED districts; GTA V draws every
 * far light as a small instanced quad — "you can drive towards them and find the bulb"; MSFS places its night lights
 * procedurally from the road and building data and keeps camera-facing light sprites for the middle distance). Street
 * lights are the one CONSTANT source over the night (≈55–77 % of a town's sky glow); residential windows are ~22–27 % at
 * dusk and fall steadily to ~04:00 (Bará et al. 2017, A Coruña / Arteixo); at most ~50 % of windows are ever lit.
 *
 * THE POINTS (`siteLightPoints`): street lamps along every street edge of the map file at the street-furniture
 * planner's class spacing (`LAMP_SPACING_M` ≡ street_furniture.ts LAMP_SPACING; arterials both sides staggered),
 * offset to the kerb, a head at 5.6 m; lit-window CLUSTERS on every face of every building (a cluster stands for
 * `CLUSTER_AREA_M2` of glazing; the glazing share and the window luminance by the building's function and type; face-on
 * intensity = L_window × glazing area, a cosine lobe about the face normal); floodlit landmark facades; a few teal
 * bio-glass fixtures (settler tech) for variety. Colour: sodium amber or LED white by the civilisation (Aurelian
 * warmer, Borean whiter), pulled toward the site's skyglow tint so the carpet agrees with its dome.
 *
 * THE ENCODING (8 bytes a point, two unorm8x4 attributes): [r, g, b, thr] [cls, facing, logI, nearFade].
 * r,g,b = chroma (the shader renormalises luminance to 1) · thr = the lit threshold (a point is lit while
 * thr/254 < the class's lit fraction; AGGREGATE_THR = the fraction SCALES the intensity instead) · cls = LIGHT_CLASS ·
 * facing = 0 omni, else a horizontal normal angle · logI = log-coded candela (0 = dead) · nearFade = metres / 16 (the
 * module writes it: inside it the real lamps and windows of a built site take over).
 *
 * THE PHYSICS (mirrored in the shader): a point of intensity I (cd) at distance d gives the eye E = I·T(d)/d² lux
 * (T = the scene fog's own transmittance, so a light fades exactly like the ground it stands on); drawn as a Gaussian
 * dot of fixed pixel size σ, its peak luminance is E / (Ω_px · 2πσ²) — flux conserved at any distance or resolution
 * (the SGI "simulating small light sources" rule: constant energy, not constant size). Into the scene through
 * photometry.emissiveNits every frame (THE PHYSICAL-EMISSIVE CONTRACT).
 */
import type { MapFile, MapSite } from './map_structures.js';
import { MAP_B_STRIDE, categoryOfFunction } from './map_structures.js';
import { footprintOf } from '../../../shared/types/structures.js';

// ── Classes and the encoding ─────────────────────────────────────────────────────────────────────────────────────
export const LIGHT_CLASS = { lamp: 0, lampLocal: 1, bio: 2, winHome: 3, winTrade: 4, winWork: 5, winCivic: 6, flood: 7 } as const;
export type LightClass = (typeof LIGHT_CLASS)[keyof typeof LIGHT_CLASS];
export const isWindowClass = (c: number): boolean => c >= 3 && c <= 6;
/** Bytes per point: [r, g, b, thr] [cls, facing, logI, nearFade]. */
export const POINT_BYTES = 8;
/** `thr` of an aggregated point: the class's lit fraction SCALES its intensity (no per-point toggle). */
export const AGGREGATE_THR = 255;
/** The log-coded intensity range: byte 1 = 1 cd … byte 255 = 10^6 cd (5.6 % a step); byte 0 = a dead slot. */
export const LOG_I_DECADES = 6;

export function encodeIntensity(cd: number): number {
  if (!(cd > 0)) return 0;
  return Math.max(1, Math.min(255, Math.round((Math.log10(Math.max(1, cd)) / LOG_I_DECADES) * 254) + 1));
}
export function decodeIntensity(b: number): number { return b <= 0 ? 0 : Math.pow(10, ((b - 1) / 254) * LOG_I_DECADES); }
/** A horizontal facing normal (nx, nz) → byte 1..255 (0 = omni). */
export function encodeFacing(nx: number, nz: number): number {
  const a = Math.atan2(nz, nx); // −π … π
  return Math.max(1, Math.min(255, Math.round(((a + Math.PI) / (2 * Math.PI)) * 254) + 1));
}
export function decodeFacing(b: number): [number, number] | null {
  if (b <= 0) return null;
  const a = ((b - 1) / 254) * 2 * Math.PI - Math.PI;
  return [Math.cos(a), Math.sin(a)];
}

// ── The fixtures ─────────────────────────────────────────────────────────────────────────────────────────────────
/** The street-furniture planner's lamp spacing by road class (street_furniture.ts LAMP_SPACING — mirrored, pinned). */
export const LAMP_SPACING_M: Record<string, number> = { arterial: 26, collector: 30, local: 34 };
/** Rural streets are lit sparsely: the spacing by settlement tier. */
export const LAMP_SPACING_TIER: Record<string, number> = { hamlet: 1.8, town: 1.15, circuit: 1.1 };
/**
 * A street head's intensity toward a FAR, near-horizontal viewer (cd). Semi-cutoff fixtures may send ≤ 200 cd per 1000
 * lamp lumens at 80° and ≤ 50 at 90° from nadir (IES); a 10–25 klm head → a few hundred cd toward the horizon.
 */
export const LAMP_CD: Record<string, number> = { arterial: 420, collector: 300, local: 220 };
export const LAMP_HEAD_M = 5.6;
/** The lit pavement pool a head lights, seen from straight above (cd): ≈1 nit (20 lx × 0.15 / π) × ≈360 m². */
export const POOL_CD = 360;
/** Share of fixtures that are settler-tech bio-glass (teal) — a little variety in the carpet. */
export const BIO_SHARE = 0.02;
export const BIO_CD = 180;
/** Glazing share of a facade by building type (map `types`). */
export const GLAZING: Record<string, number> = { skyscraper: 0.45, office: 0.4, shop: 0.42, apartment: 0.28, house: 0.2, factory: 0.1, warehouse: 0.06, '': 0.25 };
/**
 * A lit window's AVERAGE luminance seen from outside (cd/m²): the lit room's surfaces (~200 lx × ρ 0.5 / π ≈ 30 cd/m²)
 * through glass, curtains and blinds half the time, a visible fixture now and then. photometry EMISSIVE_NITS.window = 120
 * is a window with its lamp in view — the bright end; a cluster stands for many windows, so it takes the mean.
 */
export const WINDOW_NITS: Record<number, number> = { 3: 25, 4: 45, 5: 20, 6: 30 };
/** Glazing area one window cluster stands for (m²), and the most clusters a face carries (level 0). */
export const CLUSTER_AREA_M2 = 140;
export const CLUSTER_MAX_PER_FACE = 5;
/** A floodlit landmark facade's luminance (cd/m²) and the share of its face the floods reach. */
export const FLOOD_NITS = 6;
export const FLOOD_SHARE = 0.35;
/** The lit clusters stand this far out of the outline (the map's lowest ladder band) so a real facade never hides them. */
export const FACE_OUT_M = 0.8;

/** Linear-RGB fixture colours (luminance ≈ 1). */
export const LIGHT_RGB = {
  sodium: [1.75, 0.78, 0.16] as [number, number, number],
  led3000: [1.3, 0.96, 0.62] as [number, number, number],
  led4000: [1.12, 1.0, 0.86] as [number, number, number],
  roomWarm: [1.48, 0.89, 0.42] as [number, number, number],
  roomCool: [1.06, 1.0, 0.94] as [number, number, number],
  bio: [0.35, 1.08, 1.12] as [number, number, number],
  flood: [1.18, 1.0, 0.78] as [number, number, number],
};
/** Share of street heads that are sodium amber (the rest LED) by civilisation: Aurelian warmer, Borean whiter. */
export const SODIUM_SHARE: Record<string, number> = { a: 0.85, b: 0.3, '': 0.65 };
/** How far a point's colour is pulled toward its site's skyglow tint (so the carpet agrees with the dome). */
export const TINT_PULL = 0.3;

// ── Hashing (deterministic per site + index) ─────────────────────────────────────────────────────────────────────
function hash32(a: number, b: number, c = 0): number {
  let h = (Math.imul(a | 0, 0x9e3779b1) ^ Math.imul(b | 0, 0x85ebca6b) ^ Math.imul(c | 0, 0xc2b2ae35)) >>> 0;
  h ^= h >>> 16; h = Math.imul(h, 0x7feb352d) >>> 0; h ^= h >>> 15; h = Math.imul(h, 0x846ca68b) >>> 0; h ^= h >>> 16;
  return h >>> 0;
}
const h01 = (a: number, b: number, c = 0): number => hash32(a, b, c) / 4294967296;

// ── The point set ────────────────────────────────────────────────────────────────────────────────────────────────
export interface LightPoints {
  n: number;
  /** x, y, z per point: SITE-LOCAL x/z (metres from the site centre), y = metres above the site's ground. */
  xyz: Float32Array;
  /** POINT_BYTES per point. */
  bytes: Uint8Array;
}
export interface SiteLightInput {
  /** Settlement tier (city / town / hamlet / capital / district / circuit). */
  tier: string;
  /** Civilisation of the site ('a' Aurelian, 'b' Borean). */
  civ?: 'a' | 'b' | '';
  /** The site's skyglow tint (light_pollution LightSource.tint); the carpet's colours lean toward it. */
  tint?: readonly [number, number, number];
}

class PointWriter {
  n = 0;
  xyz: Float32Array;
  bytes: Uint8Array;
  constructor(cap: number) { this.xyz = new Float32Array(cap * 3); this.bytes = new Uint8Array(cap * POINT_BYTES); }
  private grow(): void {
    const xyz = new Float32Array(this.xyz.length * 2); xyz.set(this.xyz); this.xyz = xyz;
    const b = new Uint8Array(this.bytes.length * 2); b.set(this.bytes); this.bytes = b;
  }
  push(x: number, y: number, z: number, rgb: readonly number[], thr: number, cls: number, facing: number, cd: number): void {
    if (this.n * 3 + 3 > this.xyz.length) this.grow();
    const i = this.n++;
    this.xyz[i * 3] = x; this.xyz[i * 3 + 1] = y; this.xyz[i * 3 + 2] = z;
    const m = Math.max(1e-6, rgb[0], rgb[1], rgb[2]);
    const o = i * POINT_BYTES;
    this.bytes[o] = Math.round((Math.max(0, rgb[0]) / m) * 255);
    this.bytes[o + 1] = Math.round((Math.max(0, rgb[1]) / m) * 255);
    this.bytes[o + 2] = Math.round((Math.max(0, rgb[2]) / m) * 255);
    this.bytes[o + 3] = thr;
    this.bytes[o + 4] = cls;
    this.bytes[o + 5] = facing;
    this.bytes[o + 6] = encodeIntensity(cd);
    this.bytes[o + 7] = 0;
  }
  done(): LightPoints { return { n: this.n, xyz: this.xyz.slice(0, this.n * 3), bytes: this.bytes.slice(0, this.n * POINT_BYTES) }; }
}

function mixRgb(a: readonly number[], b: readonly number[], t: number, jit: number): number[] {
  return [0, 1, 2].map((k) => (a[k] + (b[k] - a[k]) * t) * (1 + jit * (k === 2 ? -1 : 1)));
}

/** The window class of a building from its plan FUNCTION (map `fns`) and whether it is a landmark. */
export function windowClassOf(fn: string, landmark: boolean): number {
  if (landmark) return LIGHT_CLASS.winCivic;
  const cat = categoryOfFunction(fn);
  if (cat === 'residential') return LIGHT_CLASS.winHome;
  if (cat === 'commerce' || cat === 'leisure') return LIGHT_CLASS.winTrade;
  if (cat === 'industry' || cat === 'energy' || cat === 'farming' || cat === 'military') return LIGHT_CLASS.winWork;
  return LIGHT_CLASS.winCivic; // civic, science, health, port …
}

/**
 * Every light of one site at level 0 (full detail), site-local. Deterministic in the site id. ~4 k points for a city,
 * ~600 for a town, ~100 for a hamlet (measured by `verify:city-lights`).
 */
export function siteLightPoints(site: MapSite, file: Pick<MapFile, 'fns' | 'types' | 'roadClasses'>, inp: SiteLightInput): LightPoints {
  const out = new PointWriter(1024);
  const civ = inp.civ ?? '';
  const tint = inp.tint ?? LIGHT_RGB.roomWarm;
  const sid = site.id | 0;
  const sodium = SODIUM_SHARE[civ] ?? SODIUM_SHARE[''];
  const spacingMul = LAMP_SPACING_TIER[inp.tier] ?? 1;
  const tintPull = (rgb: readonly number[]): number[] => mixRgb(rgb, tint, TINT_PULL, 0);

  // ── street lamps along every edge ──
  const r = site.roads;
  // Every carriageway in a 24 m grid: a head that lands on ANY street (a junction, a close parallel edge) is dropped,
  // as the street-furniture planner refuses one (verify:city-lights found 17 % on another edge's carriageway).
  const ROAD_CELL = 24;
  const roadGrid = new Map<number, number[]>();
  const segs: number[] = [];
  for (let i = 0; i + 2 < r.length;) {
    const n = r[i] | 0, hw = r[i + 1];
    for (let k = 0; k + 1 < n; k++) {
      const ax = r[i + 3 + 2 * k], az = r[i + 4 + 2 * k], bx = r[i + 5 + 2 * k], bz = r[i + 6 + 2 * k];
      const si = segs.length / 5;
      segs.push(ax, az, bx, bz, hw);
      const x0 = Math.floor((Math.min(ax, bx) - hw) / ROAD_CELL), x1 = Math.floor((Math.max(ax, bx) + hw) / ROAD_CELL);
      const z0 = Math.floor((Math.min(az, bz) - hw) / ROAD_CELL), z1 = Math.floor((Math.max(az, bz) + hw) / ROAD_CELL);
      for (let gx = x0; gx <= x1; gx++) for (let gz = z0; gz <= z1; gz++) { const key = gx * 65536 + gz; const l = roadGrid.get(key); if (l) l.push(si); else roadGrid.set(key, [si]); }
    }
    i += 3 + 2 * n;
  }
  const onCarriageway = (x: number, z: number): boolean => {
    const l = roadGrid.get(Math.floor(x / ROAD_CELL) * 65536 + Math.floor(z / ROAD_CELL));
    if (!l) return false;
    for (const si of l) {
      const ax = segs[si * 5], az = segs[si * 5 + 1], dx = segs[si * 5 + 2] - ax, dz = segs[si * 5 + 3] - az, hw = segs[si * 5 + 4];
      const L2 = dx * dx + dz * dz;
      const t = L2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / L2)) : 0;
      if (Math.hypot(x - ax - dx * t, z - az - dz * t) < hw + 0.3) return true;
    }
    return false;
  };
  let edge = 0;
  for (let i = 0; i + 2 < r.length; edge++) {
    const n = r[i] | 0, hw = r[i + 1], cls = file.roadClasses[r[i + 2] | 0] ?? 'local';
    const p0 = i + 3;
    i = p0 + 2 * n;
    if (n < 2) continue;
    const S = (LAMP_SPACING_M[cls] ?? 34) * spacingMul;
    const both = cls === 'arterial';
    const step = both ? S / 2 : S;
    const off = hw + 1.0;
    const baseCd = LAMP_CD[cls] ?? LAMP_CD.local;
    let acc = 0;
    let next = step * (0.5 + 0.4 * h01(sid, edge, 1));
    let k = 0;
    for (let s = 0; s + 1 < n; s++) {
      const ax = r[p0 + 2 * s], az = r[p0 + 2 * s + 1], bx = r[p0 + 2 * s + 2], bz = r[p0 + 2 * s + 3];
      const L = Math.hypot(bx - ax, bz - az);
      if (!(L > 0.01)) continue;
      const tx = (bx - ax) / L, tz = (bz - az) / L;
      while (next <= acc + L) {
        const t = next - acc;
        // One side for a local street; staggered sides for a collector and an arterial (both sides at half spacing).
        const side = cls === 'local' ? 1 : (k % 2 === 0 ? 1 : -1);
        const x = ax + tx * t + -tz * off * side, z = az + tz * t + tx * off * side;
        if (onCarriageway(x, z)) { k++; next += step; continue; }
        const hh = h01(sid, edge * 4099 + k, 2);
        const bio = hh < BIO_SHARE;
        const rgb = bio ? LIGHT_RGB.bio : tintPull(h01(sid, edge * 4099 + k, 3) < sodium ? LIGHT_RGB.sodium : (civ === 'b' ? LIGHT_RGB.led4000 : LIGHT_RGB.led3000));
        const jit = (h01(sid, edge * 4099 + k, 4) - 0.5) * 0.12;
        const cd = (bio ? BIO_CD : baseCd) * (0.85 + 0.3 * h01(sid, edge * 4099 + k, 5));
        out.push(x, LAMP_HEAD_M + jit * 3, z, rgb.map((v, ci) => v * (1 + (ci === 0 ? jit : -jit))), Math.floor(hh * 254), bio ? LIGHT_CLASS.bio : (cls === 'local' ? LIGHT_CLASS.lampLocal : LIGHT_CLASS.lamp), 0, cd);
        k++;
        next += step;
      }
      acc += L;
    }
  }

  // ── lit-window clusters (and floodlit landmark faces) on every face ──
  const b = site.b;
  // The outlines in a 32 m grid: a cluster pushed out of its face INTO a touching neighbour stands behind that wall.
  const B_CELL = 32;
  const bGrid = new Map<number, number[]>();
  for (let j = 0; j + MAP_B_STRIDE <= b.length; j += MAP_B_STRIDE) {
    const rr = Math.hypot(b[j + 2], b[j + 3]) / 2;
    for (let gx = Math.floor((b[j] - rr) / B_CELL); gx <= Math.floor((b[j] + rr) / B_CELL); gx++) for (let gz = Math.floor((b[j + 1] - rr) / B_CELL); gz <= Math.floor((b[j + 1] + rr) / B_CELL); gz++) {
      const key = gx * 65536 + gz; const l = bGrid.get(key); if (l) l.push(j); else bGrid.set(key, [j]);
    }
  }
  const insideOther = (x: number, z: number, self: number): boolean => {
    const l = bGrid.get(Math.floor(x / B_CELL) * 65536 + Math.floor(z / B_CELL));
    if (!l) return false;
    for (const j of l) {
      if (j === self) continue;
      const c = Math.cos(b[j + 4]), s = Math.sin(b[j + 4]);
      const dx = x - b[j], dz = z - b[j + 1];
      if (Math.abs(dx * c - dz * s) <= b[j + 2] / 2 && Math.abs(dx * s + dz * c) <= b[j + 3] / 2) return true;
    }
    return false;
  };
  for (let j = 0; j + MAP_B_STRIDE <= b.length; j += MAP_B_STRIDE) {
    const lx = b[j], lz = b[j + 1], w = b[j + 2], d = b[j + 3], yaw = b[j + 4], h = b[j + 5];
    // kind: 0 a building, 1 a landmark (windows + floodlit faces), 2 a MONUMENT (floodlit faces only — a statue or a
    // fountain has no rooms; agent P 2026-10-06, the player cities). The baked map file holds 0 and 1 only.
    const kind = b[j + 8] | 0;
    const fn = file.fns[b[j + 6] | 0] ?? '', type = file.types[b[j + 7] | 0] ?? '', landmark = kind === 1, monument = kind === 2;
    if (!(w > 0.5) || !(d > 0.5) || !(h > 1)) continue;
    const bi = j / MAP_B_STRIDE;
    const cls = windowClassOf(fn, landmark);
    const glazing = landmark ? 0.15 : (GLAZING[type] ?? GLAZING['']);
    const nits = WINDOW_NITS[cls] ?? 120;
    const c = Math.cos(yaw), s = Math.sin(yaw);
    // The four faces in three's rotation.y convention (local +x → (cos, −sin), local +z → (sin, cos)).
    const faces: Array<[number, number, number, number]> = [ // [local nx, local nz, half depth to the face, face width]
      [1, 0, w / 2, d], [-1, 0, w / 2, d], [0, 1, d / 2, w], [0, -1, d / 2, w],
    ];
    // Rooms are lit warm (incandescent / warm LED) with a share of cool white offices and workshops — the carpet reads
    // amber with white districts, as Athens does from above (NOIRLab).
    const warmShare = cls === LIGHT_CLASS.winHome ? 0.9 : cls === LIGHT_CLASS.winWork ? 0.35 : 0.6;
    const roomRgb = h01(sid, bi, 7) < warmShare ? LIGHT_RGB.roomWarm : (cls === LIGHT_CLASS.winWork || civ === 'b' ? LIGHT_RGB.led4000 : LIGHT_RGB.led3000);
    for (let f = 0; f < 4; f++) {
      const [nxl, nzl, half, fw] = faces[f];
      const nx = nxl * c + nzl * s, nz = -nxl * s + nzl * c; // the face normal, site frame
      const tx = -nz, tz = nx; // along the face
      const cx = lx + nx * (half + FACE_OUT_M), cz = lz + nz * (half + FACE_OUT_M);
      const facing = encodeFacing(nx, nz);
      const tall = Math.max(1, h - 1.2);
      const glass = fw * tall * glazing;
      const nCl = Math.max(1, Math.min(CLUSTER_MAX_PER_FACE, Math.round(glass / CLUSTER_AREA_M2)));
      const rows = Math.max(1, Math.min(nCl, Math.round(Math.sqrt((nCl * tall) / Math.max(1, fw)))));
      const cols = Math.ceil(nCl / rows);
      const cd = (nits * glass) / nCl;
      for (let q = 0; q < (monument ? 0 : nCl); q++) {
        const ci = q % cols, ri = Math.floor(q / cols);
        const hu = h01(sid, bi * 64 + f * 16 + q, 8), hv = h01(sid, bi * 64 + f * 16 + q, 9);
        const u = ((ci + 0.2 + 0.6 * hu) / cols - 0.5) * fw * 0.9;
        const y = Math.min(h - 0.6, 2.2 + ((ri + 0.2 + 0.6 * hv) / rows) * Math.max(0.4, h - 3.2));
        if (insideOther(cx + tx * u, cz + tz * u, j)) continue;
        const bio = h01(sid, bi * 64 + f * 16 + q, 10) < BIO_SHARE * 0.6;
        const rgb = bio ? LIGHT_RGB.bio : tintPull(roomRgb);
        const thr = Math.floor(h01(sid, bi * 64 + f * 16 + q, 11) * 254);
        out.push(cx + tx * u, y, cz + tz * u, rgb, thr, bio ? LIGHT_CLASS.bio : cls, facing, cd * (0.8 + 0.4 * h01(sid, bi * 64 + f * 16 + q, 12)));
      }
      if ((landmark || monument) && !insideOther(cx, cz, j)) {
        // A floodlit face: one point low on the face, its lit area's flux (the floods light the lower third).
        const lit = fw * Math.min(h, 60) * FLOOD_SHARE;
        out.push(cx, Math.min(h * 0.25, 20), cz, tintPull(LIGHT_RGB.flood), 0, LIGHT_CLASS.flood, facing, FLOOD_NITS * lit);
      }
    }
  }
  return out.done();
}

/**
 * The bytes of a point on a REAL lamp head (a built town's registered anchor — StreetLampsModule.litLampsOf), coloured
 * and graded like the carpet's own street heads (deterministic in the site and the head's index).
 */
export function realLampBytes(siteId: number, index: number, civ: 'a' | 'b' | '', tint?: readonly [number, number, number]): Uint8Array {
  const w = new PointWriter(1);
  const sodium = SODIUM_SHARE[civ] ?? SODIUM_SHARE[''];
  const base = h01(siteId, index, 13) < sodium ? LIGHT_RGB.sodium : (civ === 'b' ? LIGHT_RGB.led4000 : LIGHT_RGB.led3000);
  const rgb = mixRgb(base, tint ?? LIGHT_RGB.roomWarm, TINT_PULL, 0);
  w.push(0, 0, 0, rgb, 0, LIGHT_CLASS.lamp, 0, LAMP_CD.collector * (0.85 + 0.3 * h01(siteId, index, 14)));
  return w.done().bytes;
}

// ── Player cities (agent P, 2026-10-06: "player cities get no city lights yet") ──────────────────────────────────────
/**
 * How a player structure lights the night. A player city is a runtime component (deeds, never a bake), so its carpet is
 * built from its `structure` rows: each deed stands in the baked towns' building path as one outline — the footprint
 * (`STRUCTURE_FOOTPRINT`, the cooked bbox × the catalog scale) turned by the ROW's yaw in three's rotation.y convention
 * (the model yaw `modelYawOf` turns the MODEL by π, never the footprint frame) — with a plan function and building type
 * that pick its glazing, window luminance and class exactly as a baked town's (`GLAZING`, `WINDOW_NITS`,
 * `windowClassOf`). A LANDMARK (the City Hall) is windows + floodlit faces as L draws a town's landmark; a MONUMENT
 * (statue, fountain, the obelisk) floodlit faces only; a `street_lamp` is one head (`'lamp'`). Anything else is dark:
 * walls, turrets, gardens, pools, the console, the tent and the machines (extractors, harvester).
 */
export type PlayerLight = { fn: string; type: string; kind: 0 | 1 | 2 } | 'lamp';
export const PLAYER_STRUCTURE_LIGHT: Readonly<Record<string, PlayerLight>> = {
  small_house: { fn: 'habitation', type: 'house', kind: 0 },
  medium_house: { fn: 'habitation', type: 'house', kind: 0 },
  large_house: { fn: 'habitation', type: 'house', kind: 0 },
  xlarge_house: { fn: 'habitation_luxury', type: 'house', kind: 0 },
  hotel: { fn: 'habitation', type: 'apartment', kind: 0 },
  city_hall: { fn: 'civic', type: 'office', kind: 1 },
  museum: { fn: 'civic', type: 'office', kind: 0 },
  guild_hall: { fn: 'civic', type: 'office', kind: 0 },
  hospital: { fn: 'medical', type: 'office', kind: 0 },
  cloning: { fn: 'science', type: 'office', kind: 0 },
  bank: { fn: 'corporate', type: 'office', kind: 0 },
  cantina: { fn: 'entertainment', type: 'shop', kind: 0 },
  theater: { fn: 'entertainment', type: 'shop', kind: 0 },
  store: { fn: 'commerce', type: 'shop', kind: 0 },
  salon: { fn: 'commerce', type: 'shop', kind: 0 },
  market: { fn: 'market', type: 'shop', kind: 0 },
  garage: { fn: 'repair', type: 'warehouse', kind: 0 },
  shuttleport: { fn: 'landing', type: 'warehouse', kind: 0 },
  spaceport: { fn: 'landing', type: 'warehouse', kind: 0 },
  factory: { fn: 'manufacturing', type: 'factory', kind: 0 },
  mining_facility: { fn: 'industry', type: 'factory', kind: 0 },
  bunker: { fn: 'defense', type: 'warehouse', kind: 0 },
  outpost: { fn: 'defense', type: 'warehouse', kind: 0 },
  statue: { fn: 'civic', type: '', kind: 2 },
  fountain: { fn: 'civic', type: '', kind: 2 },
  civic_decoration: { fn: 'civic', type: '', kind: 2 },
  street_lamp: 'lamp',
};
export function playerStructureLight(type: string): PlayerLight | null { return PLAYER_STRUCTURE_LIGHT[type] ?? null; }
/** Does this structure type give light at night (a lit building, a floodlit monument or a lamp)? */
export function isLitPlayerStructure(type: string): boolean { return type in PLAYER_STRUCTURE_LIGHT; }
/**
 * The deed lamp's head over its base, as a share of the model's height: the teal lantern fills the top eighth of the
 * 4.5 m post (LOOKED AT: out-player-city/images/street_lamp.png) — its centre at 0.88.
 */
export const PLAYER_LAMP_HEAD_FRAC = 0.88;
/** The hashing site id of a player city (a namespace apart from the baked sites' 0…374). */
export function playerCitySiteId(cityId: number): number { return (hash32(0x50c17, cityId | 0) & 0x3fffffff) | 0x40000000; }
/** The hashing site id of one player structure: its jitter is its own, stable whatever else is built or taken down. */
export function playerStructureSiteId(cityId: number, structureId: number): number { return (hash32(cityId | 0, structureId | 0, 0x5f2) & 0x3fffffff) | 0x40000000; }

export interface PlayerStructureLite { id: number; type: string; x: number; z: number; yaw: number }
export interface PlayerCityLightInput {
  cityId: number;
  /** The city's centre (the City Hall), metres absolute. Points come back relative to it. */
  centerX: number; centerZ: number;
  civ?: 'a' | 'b' | '';
  /** The city's skyglow tint (light_pollution `tintOfCiv`); the carpet's colours lean toward it as a town's do. */
  tint?: readonly [number, number, number];
  structures: ReadonlyArray<PlayerStructureLite>;
  /** The ground height (absolute) at a point — a player city is NOT on a plateau, so every structure stands on its own. */
  groundAt: (x: number, z: number) => number;
}
export interface PlayerCityLights {
  /** Level-0 points: x / z relative to the centre, y over the CENTRE's ground (`groundY`) — each structure's own ground folded in. */
  points: LightPoints;
  groundY: number;
  /** The lamp heads, ABSOLUTE (StreetLampsModule's anchors: the near head glows and the pooled lights). */
  lamps: Array<{ x: number; y: number; z: number }>;
  buildings: number; monuments: number;
  /** Structures that give light (buildings + monuments + lamps) — the skyglow's build count. */
  lit: number;
}

const PLAYER_FNS: string[] = [];
const PLAYER_TYPES: string[] = [];
for (const v of Object.values(PLAYER_STRUCTURE_LIGHT)) {
  if (v === 'lamp') continue;
  if (!PLAYER_FNS.includes(v.fn)) PLAYER_FNS.push(v.fn);
  if (!PLAYER_TYPES.includes(v.type)) PLAYER_TYPES.push(v.type);
}
const PLAYER_FILE: Pick<MapFile, 'fns' | 'types' | 'roadClasses'> = { fns: PLAYER_FNS, types: PLAYER_TYPES, roadClasses: [] };

/**
 * Every light of one player city at level 0 (full detail), relative to its centre. Each structure goes through the baked
 * building path ALONE (siteLightPoints over a one-building site): deeds never touch (THE FOOTPRINT TEST keeps a 1 m gap,
 * wider than a cluster's FACE_OUT_M), so no neighbour can hide a cluster, and each keeps its own jitter. A lamp is one
 * head coloured and graded like a built town's REAL heads (`realLampBytes`). Deterministic in the city and structure ids.
 */
export function playerCityLightPoints(inp: PlayerCityLightInput): PlayerCityLights {
  const g0 = inp.groundAt(inp.centerX, inp.centerZ);
  const groundY = Number.isFinite(g0) ? g0 : 0;
  const civ = inp.civ ?? '';
  const parts: LightPoints[] = [];
  const lamps: Array<{ x: number; y: number; z: number }> = [];
  let buildings = 0, monuments = 0;
  const sorted = [...inp.structures].sort((a, b) => a.id - b.id);
  const lampHeadM = footprintOf('street_lamp').h * PLAYER_LAMP_HEAD_FRAC;
  for (const s of sorted) {
    const light = PLAYER_STRUCTURE_LIGHT[s.type];
    if (!light || !Number.isFinite(s.x) || !Number.isFinite(s.z)) continue;
    const gs = inp.groundAt(s.x, s.z);
    const dy = (Number.isFinite(gs) ? gs : groundY) - groundY;
    const lx = s.x - inp.centerX, lz = s.z - inp.centerZ;
    if (light === 'lamp') {
      const one = realLampBytes(playerCitySiteId(inp.cityId), s.id, civ, inp.tint);
      parts.push({ n: 1, xyz: new Float32Array([lx, dy + lampHeadM, lz]), bytes: one });
      lamps.push({ x: s.x, y: groundY + dy + lampHeadM, z: s.z });
      continue;
    }
    const fp = footprintOf(s.type);
    const yaw = Number.isFinite(s.yaw) ? s.yaw : 0;
    const site: MapSite = {
      id: playerStructureSiteId(inp.cityId, s.id), cx: 0, cz: 0, roads: [], gates: [],
      b: [lx, lz, fp.hx * 2, fp.hz * 2, yaw, fp.h, PLAYER_FNS.indexOf(light.fn), PLAYER_TYPES.indexOf(light.type), light.kind, 0],
    };
    const p = siteLightPoints(site, PLAYER_FILE, { tier: 'town', civ, tint: inp.tint });
    if (dy !== 0) for (let i = 0; i < p.n; i++) p.xyz[i * 3 + 1] += dy;
    parts.push(p);
    if (light.kind === 2) monuments++; else buildings++;
  }
  let n = 0; for (const p of parts) n += p.n;
  const xyz = new Float32Array(n * 3), bytes = new Uint8Array(n * POINT_BYTES);
  let o = 0;
  for (const p of parts) { xyz.set(p.xyz.subarray(0, p.n * 3), o * 3); bytes.set(p.bytes.subarray(0, p.n * POINT_BYTES), o * POINT_BYTES); o += p.n; }
  return { points: { n, xyz, bytes }, groundY, lamps, buildings, monuments, lit: buildings + monuments + lamps.length };
}

// ── Level of detail: aggregation into cells ───────────────────────────────────────────────────────────────────────
/** Cell size (m) of each level: 0 = every point; a coarser level merges a cell's points into one per class group. */
export const LEVEL_CELL_M = [0, 40, 100, 250] as const;
/** Distance (m) from which each level is wanted: a cell ≲ 2 px of a 600 px-high 70° view (p ≈ d · 0.0024 m). */
export const LEVEL_FROM_M = [0, 8000, 20000, 45000] as const;
export const LEVELS = 4;

/**
 * Merge a point set into `cell`-metre cells: per cell one point per group (street fixtures; bio-glass; each window
 * class; floods), at the intensity-weighted centroid with the summed intensity — FLUX IS CONSERVED. Members facing one
 * way keep the face's lobe; mixed facings become omni at the lobe's azimuthal mean (1/π of face-on). Window aggregates
 * carry AGGREGATE_THR: the class's lit fraction scales them (the toggles average out).
 */
export function aggregateLights(src: LightPoints, cell: number): LightPoints {
  if (!(cell > 0)) return src;
  interface Acc { w: number; x: number; y: number; z: number; r: number; g: number; b: number; cls: number; face: number; mixed: boolean; win: boolean }
  const cells = new Map<string, Acc>();
  for (let i = 0; i < src.n; i++) {
    const o = i * POINT_BYTES;
    const cls = src.bytes[o + 4];
    const cd = decodeIntensity(src.bytes[o + 6]);
    if (!(cd > 0)) continue;
    const x = src.xyz[i * 3], y = src.xyz[i * 3 + 1], z = src.xyz[i * 3 + 2];
    const grp = cls <= 1 ? 0 : cls; // the two street classes merge; every other class keeps its own group
    const key = `${Math.floor(x / cell)},${Math.floor(z / cell)},${grp}`;
    let a = cells.get(key);
    const face = src.bytes[o + 5];
    if (!a) { a = { w: 0, x: 0, y: 0, z: 0, r: 0, g: 0, b: 0, cls, face, mixed: false, win: isWindowClass(cls) }; cells.set(key, a); }
    if (Math.abs(face - a.face) > 1) a.mixed = true;
    // A point that toggles on its threshold contributes its EXPECTED share once aggregated: the fraction is applied
    // by the shader to the aggregate, so a window cluster enters at its full face-on intensity here.
    const r = src.bytes[o], g = src.bytes[o + 1], bb = src.bytes[o + 2];
    const lum = 0.2126 * r + 0.7152 * g + 0.0722 * bb || 1;
    a.w += cd; a.x += x * cd; a.y += y * cd; a.z += z * cd;
    a.r += (r / lum) * cd; a.g += (g / lum) * cd; a.b += (bb / lum) * cd;
  }
  const out = new PointWriter(Math.max(16, cells.size));
  for (const a of cells.values()) {
    const omni = a.mixed || a.face === 0;
    const cd = omni && a.face !== 0 ? a.w / Math.PI : a.w;
    out.push(a.x / a.w, a.y / a.w, a.z / a.w, [a.r / a.w, a.g / a.w, a.b / a.w], a.win ? AGGREGATE_THR : 0, a.cls, omni ? 0 : a.face, cd);
  }
  return out.done();
}

/** All four levels of a site (level 0 as generated). */
export function siteLightLevels(pts0: LightPoints): LightPoints[] {
  return LEVEL_CELL_M.map((c, i) => (i === 0 ? pts0 : aggregateLights(pts0, c)));
}

/** The level a site wants at `distM` (the camera's distance to its nearest edge). */
export function levelForDistance(distM: number): number {
  let L = 0;
  for (let i = 1; i < LEVELS; i++) if (distM >= LEVEL_FROM_M[i]) L = i;
  return L;
}

/**
 * THE BUDGET: start from each site's wanted level and coarsen the FARTHEST sites first, one level at a time, until the
 * total fits `budget` points (the nearest sites keep their detail). Returns the chosen level per input (same order).
 */
export function chooseLevels(sites: ReadonlyArray<{ dist: number; counts: readonly number[] }>, budget: number): number[] {
  const lv = sites.map((s) => levelForDistance(s.dist));
  let total = sites.reduce((acc, s, i) => acc + s.counts[lv[i]], 0);
  if (total <= budget) return lv;
  const order = sites.map((_, i) => i).sort((a, b) => sites[b].dist - sites[a].dist);
  for (let pass = 0; pass < LEVELS && total > budget; pass++) {
    for (const i of order) {
      if (total <= budget) break;
      if (lv[i] >= LEVELS - 1) continue;
      total -= sites[i].counts[lv[i]] - sites[i].counts[lv[i] + 1];
      lv[i]++;
    }
  }
  return lv;
}

// ── The night schedule ───────────────────────────────────────────────────────────────────────────────────────────
/**
 * The share of window clusters lit by local hour (0–24): [home, trade, work, civic]. Residential peaks ~50 % in the
 * evening and falls steadily to ~04:00, then rises with the early risers (Bará et al. 2017; the 50 % maximum lit-window
 * study); shops and offices empty by late evening; industry runs night shifts; civic halls keep a few lights on.
 */
export const LIT_TABLE: ReadonlyArray<readonly [number, number, number, number, number]> = [
  [0, 0.22, 0.16, 0.3, 0.18], [2, 0.11, 0.12, 0.28, 0.14], [4, 0.07, 0.1, 0.28, 0.12], [5, 0.1, 0.1, 0.3, 0.12],
  [6, 0.22, 0.14, 0.32, 0.15], [8, 0.12, 0.22, 0.3, 0.2], [12, 0.06, 0.25, 0.3, 0.2], [17, 0.25, 0.55, 0.35, 0.4],
  [19, 0.45, 0.62, 0.35, 0.45], [21, 0.5, 0.5, 0.33, 0.38], [22, 0.44, 0.38, 0.32, 0.3], [23, 0.34, 0.24, 0.31, 0.22],
];
export function litFractions(hour: number): [number, number, number, number] {
  const hh = ((hour % 24) + 24) % 24;
  const T = LIT_TABLE;
  for (let i = 0; i < T.length; i++) {
    const a = T[i], bq = T[(i + 1) % T.length];
    const h0 = a[0], h1 = i + 1 < T.length ? bq[0] : bq[0] + 24;
    const hx = hh < h0 ? hh + 24 : hh;
    if (hx >= h0 && hx <= h1) {
      const t = h1 > h0 ? (hx - h0) / (h1 - h0) : 0;
      return [1, 2, 3, 4].map((k) => a[k] + (bq[k] - a[k]) * t) as [number, number, number, number];
    }
  }
  return [T[0][1], T[0][2], T[0][3], T[0][4]];
}
/** Local hour from the sky's time of day (0 = midnight, 0.5 = noon — the probes' and the clock's convention). */
export const hourOfTod = (tod: number): number => (((tod % 1) + 1) % 1) * 24;
/** The photocells: street lights on through dusk (the sky's night factor 0 … 1). */
export function lightsOn(night: number): number {
  const t = Math.max(0, Math.min(1, (night - 0.06) / (0.3 - 0.06)));
  return t * t * (3 - 2 * t);
}

// ── The point's photometry (the shader mirrors these lines) ──────────────────────────────────────────────────────
/** The Gaussian dot's σ and the quad's half size, in RASTER pixels (constant screen size; ~2.5 px across). */
export const SPRITE_SIGMA_PX = 0.55;
export const SPRITE_HALF_PX = 1.7;
/**
 * THE KNEE: a point's luminance runs linear up to KNEE_EV stops over the scene key, then logarithmically (×0.6 a
 * natural log) to the cap. The night is displayed ~5.5 stops under its key (photometry's display drop), so a lit
 * window or a street head 5 km out is 5–10 stops over white: drawn linearly every point clips to the same white and
 * a town's carpet merges into one white bar with no hue (the bed's first pictures). The knee keeps the hierarchy
 * (a lit tower brighter than a lamp, a near lamp brighter than a far one) and the sodium amber.
 */
export const KNEE_EV = 5;
export const KNEE_SLOPE = 0.5;
export function kneeCompress(x: number): number { return x <= 1 ? x : 1 + Math.log(x) * KNEE_SLOPE; }
/** Solid angle of one raster pixel at the view centre (sr). */
export function pixelSolidAngle(fovYRad: number, heightPx: number): number {
  const p = (2 * Math.tan(fovYRad / 2)) / Math.max(1, heightPx);
  return p * p;
}
/** Illuminance at the eye from a point of `cd` at `dM` metres through the scene fog (FogExp2 density ρ: T = e^−(ρd)²). */
export function pointIlluminance(cd: number, dM: number, fogDensity: number): number {
  const d = Math.max(1, dM);
  return (cd * Math.exp(-(fogDensity * d) * (fogDensity * d))) / (d * d);
}
/** The dot's peak luminance (cd/m²) for illuminance E: flux spread over the Gaussian's 2πσ² pixels. */
export function peakNits(E: number, pxSr: number, sigmaPx = SPRITE_SIGMA_PX): number {
  return E / (Math.max(1e-12, pxSr) * 2 * Math.PI * sigmaPx * sigmaPx);
}
/** A window's cosine lobe: the projected glazing toward the viewer (0 behind the face). */
export function faceLobe(facing: [number, number] | null, toViewX: number, toViewY: number, toViewZ: number): number {
  if (!facing) return 1;
  const l = Math.hypot(toViewX, toViewY, toViewZ) || 1;
  return Math.max(0, (facing[0] * toViewX + facing[1] * toViewZ) / l);
}
/** A street head seen from above also shows its lit pool: + POOL_CD × sin(depression). */
export function lampIntensity(cd: number, sinDepression: number): number { return cd + POOL_CD * Math.max(0, sinDepression); }
/**
 * THE DEPTH PULL: a point is drawn this many metres nearer the eye than it stands, so it wins the depth test against
 * the ground and the facade it sits on. Standard depth resolves ≈ 6e-8 · d² / near metres at distance d (one 24-bit
 * step: 22 m at 4.3 km, 120 m at 10 km, 4 km at 60 km on Atlas's 0.05 m near plane), and looking DOWN on a town a lamp
 * stands only h / sin(depression) in front of the ground behind it (17 m from 20° up) — the bed's aerial view lost
 * every street head of the nearest town to the ground (verify:city-lights-bed:probe, the no-depth control lit it).
 * The pull is PULL_ULPS steps of that precision (+ 2 m + 0.3 %), capped at a quarter of d.
 */
export const PULL_ULPS = 6;
export function depthPull(dM: number, near = 0.05): number { return Math.min(dM * 0.25, 2 + 0.003 * dM + (PULL_ULPS * 6e-8 * dM * dM) / Math.max(1e-3, near)); }
/** Scintillation (terrestrial twinkling grows with the air path): the shimmer depth by distance. */
export function shimmerDepth(dM: number): number {
  const t = Math.max(0, Math.min(1, (dM - 1500) / (15000 - 1500)));
  return 0.35 * t * t * (3 - 2 * t);
}

// ── The town's own haze (the near-ground glow over a lit town, seen from outside) ─────────────────────────────────
/**
 * The haze extends a light dome (light_pollution `domeRadiance`) BELOW the horizon, onto the ground and buildings
 * behind and around the town — the sky shader draws the dome only where the SKY shows, so without this the glowing air
 * over a town stops dead at the skyline. Along a ray that hits geometry at `hitM`, the glow is the dome's horizon value
 * at the ray's bearing × an elevation fall below the horizon (the glowing layer's angular thickness from here,
 * atan(LAYER_M / d)) × the share of the town's glowing air that lies IN FRONT of the hit (a smoothstep over the town's
 * glow depth). Above the horizon it is the dome's own value (it lands on a skyline building, continuous with the sky).
 * A dome whose town the viewer stands in adds nothing here (the fog tint and the all-sky glow carry it).
 */
export const HAZE_LAYER_M = 800;
/**
 * THE HAZE'S DISPLAY (agreeing with the sky, agent S's THE ADAPTED ARTIFICIAL SKY, lib/sky/night_sky_model): the sky
 * draws its artificial part as ART_K·ln(1 + art/ART_K) units × the night-floor lift, where art = the camera's zenith
 * glow × its horizon shape + the domes. The haze over the ground is the domes' share of that at the horizon — the
 * adapted EXCESS the domes add over the uniform glow `gs` — so the glow is continuous where the skyline meets the sky.
 * (Drawn at its physical value it was 9 % of the lifted horizon: invisible — the bed, and agent S's measurement.)
 */
export const HAZE_ART_K = 2;
export function hazeDisplayUnits(H: number, gs: number, lift: number): number {
  return HAZE_ART_K * (Math.log(1 + (Math.max(0, gs) + Math.max(0, H)) / HAZE_ART_K) - Math.log(1 + Math.max(0, gs) / HAZE_ART_K)) * lift;
}
export interface HazeDome { dirX: number; dirZ: number; dist: number; peak: number; halfWidth: number; height: number; glowR: number }
export function hazeRatio(dome: HazeDome, vx: number, vy: number, vz: number, hitM: number): number {
  const h = Math.hypot(vx, vz);
  const distM = dome.dist * 1000;
  if (distM < dome.glowR) return 0;
  const c = h > 1e-6 ? (vx * dome.dirX + vz * dome.dirZ) / h : 1;
  const dAz = Math.acos(Math.max(-1, Math.min(1, c)));
  const azW = Math.exp(-0.5 * (dAz / dome.halfWidth) ** 2);
  const elev = Math.atan2(vy, Math.max(h, 1e-6));
  let prof: number;
  if (elev >= 0) {
    const top = Math.exp(-(Math.PI / 2) / dome.height);
    prof = Math.max(0, (Math.exp(-elev / dome.height) - top) / (1 - top));
  } else {
    const hDown = Math.max(0.02, Math.atan2(HAZE_LAYER_M, distM));
    prof = Math.exp(elev / hDown);
  }
  const t = Math.max(0, Math.min(1, (hitM - (distM - dome.glowR)) / (2 * dome.glowR)));
  const front = t * t * (3 - 2 * t);
  const inside = Math.max(0, Math.min(1, (distM - dome.glowR) / dome.glowR));
  return dome.peak * azW * prof * front * inside;
}
